import React, { useState } from 'react';
import NeoLoader from '../../../components/Common/NeoLoader';
import OperationalKanban from './components/OperationalKanban';
import OperationalItemSidebar from './components/OperationalItemSidebar';
import CreateTaskSidebar from './components/CreateTaskSidebar';
import DragWaitingModal from './components/DragWaitingModal';
import useOperationalMetrics from './hooks/useOperationalMetrics';
import { useOperationalKanban } from './hooks/useOperationalKanban';

const OperationalDashboard = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [createSidebarOpen, setCreateSidebarOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [scrollToOutcome, setScrollToOutcome] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const handleItemClick = (item, shouldScrollToOutcome = false) => {
        setSelectedItem(item);
        setScrollToOutcome(shouldScrollToOutcome);
        setSidebarOpen(true);
    };

    const { mySalesTodayCount, mySalesTodayTotal } = useOperationalMetrics();
    const { 
        columns, 
        loading: isLoading, 
        onDragEnd,
        dragModal,
        handleDragModalConfirm,
        handleDragModalCancel
    } = useOperationalKanban(refreshKey, handleItemClick);

    const handleTaskCreated = () => {
        setRefreshKey(prev => prev + 1);
    };

    const formatCurrency = (val) => {
        return (val || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <NeoLoader type="inline" loading={isLoading} />
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="operational-page-header mb-3">
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="d-flex align-items-center gap-3">
                        <div className="operational-header-icon">
                            <i className="mdi mdi-view-dashboard-outline font-size-20"></i>
                        </div>
                        <div>
                            <h5 className="mb-1 fw-bold text-dark">Operacional</h5>
                            <p className="mb-0 text-muted font-size-13">Tarefas, contatos e pendências em execução</p>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                        <div className="operational-sales-pill">
                            <span className="operational-sales-label">Vendas hoje</span>
                            <div className="d-flex align-items-baseline gap-2">
                                <span className="fw-bold text-dark">{String(mySalesTodayCount).padStart(2, '0')}</span>
                                <span className="text-muted font-size-12">vendas</span>
                                <span className="fw-bold text-success">{formatCurrency(mySalesTodayTotal)}</span>
                            </div>
                        </div>
                        <a
                            href="/manual_kanban_operacional.html"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                textDecoration: 'none',
                                fontFamily: "'Outfit', sans-serif",
                                fontWeight: 900,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontSize: '12.5px',
                                background: '#482712',
                                border: '2px solid #482712',
                                borderRadius: '12px',
                                color: '#ffffff',
                                padding: '8px 18px',
                                boxShadow: '0 4px 12px rgba(72, 39, 18, 0.25)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                height: '42px',
                                minWidth: '130px',
                                cursor: 'pointer',
                            }}
                        >
                            <i className="mdi mdi-book-open-outline font-size-18"></i>
                            <span>Manual de Vendas</span>
                        </a>
                        <button
                            className="neo-btn-primary"
                            onClick={() => setCreateSidebarOpen(true)}
                        >
                            <i className="mdi mdi-plus font-size-18"></i>
                            <span>Nova Tarefa</span>
                        </button>
                    </div>
                </div>
            </div>

            <OperationalKanban
                columns={columns}
                onDragEnd={onDragEnd}
                onItemClick={handleItemClick}
                onNewTaskClick={() => setCreateSidebarOpen(true)}
            />

            <OperationalItemSidebar 
                isOpen={sidebarOpen} 
                toggle={() => {
                    setSidebarOpen(!sidebarOpen);
                    if (sidebarOpen) {
                        setScrollToOutcome(false); // Reset scroll to outcome state when closing
                    }
                }} 
                item={selectedItem} 
                onRefresh={handleTaskCreated}
                scrollToOutcome={scrollToOutcome}
            />

            <CreateTaskSidebar
                isOpen={createSidebarOpen}
                toggle={() => setCreateSidebarOpen(!createSidebarOpen)}
                onTaskCreated={handleTaskCreated}
            />

            <DragWaitingModal
                isOpen={dragModal.open}
                onConfirm={handleDragModalConfirm}
                onCancel={handleDragModalCancel}
                itemName={dragModal.item?.memberName || dragModal.item?.title || ''}
            />
        </React.Fragment>
    );
};

export default OperationalDashboard;
